import React, { useState, useEffect, useRef } from 'react';
import { Card, Button, Tag, Space, Select, message, Spin } from 'antd';
import {
  EnvironmentOutlined,
  ReloadOutlined,
  FullscreenOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  CarOutlined,
} from '@ant-design/icons';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import api from '../services/api';

const { Option } = Select;

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const activeIcon = new L.Icon({
  iconUrl:
    'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-green.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const idleIcon = new L.Icon({
  iconUrl:
    'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-grey.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

function ChangeView({ center }) {
  const map = useMap();
  useEffect(() => {
    map.setView(center, map.getZoom());
  }, [center, map]);
  return null;
}

const deliveryIcon = new L.Icon({
  iconUrl:
    'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-blue.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const RealTimeLocationMap = ({ selectedProviderId, selectedDeliveryPersonId }) => {
  const [providers, setProviders] = useState([]);
  const [deliveryPersonnel, setDeliveryPersonnel] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [selectedDeliveryPerson, setSelectedDeliveryPerson] = useState(null);
  const [isTracking, setIsTracking] = useState(true);
  const [mapCenter, setMapCenter] = useState({ lat: 27.7172, lng: 85.324 });
  const [loading, setLoading] = useState(true);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [locationHistory, setLocationHistory] = useState({});
  const mapRef = useRef(null);
  const isTrackingRef = useRef(true);

  useEffect(() => {
    isTrackingRef.current = isTracking;
  }, [isTracking]);

  useEffect(() => {
    const initTracking = () => {
      try {
        setConnectionStatus('connecting');

        if (isTrackingRef.current) {
          const pollInterval = setInterval(async () => {
            if (isTrackingRef.current) {
              await fetchActiveProviders();
              await fetchDeliveryLocations();
            }
          }, 5000);

          setConnectionStatus('connected');

          return () => {
            clearInterval(pollInterval);
            setConnectionStatus('disconnected');
          };
        } else {
          fetchActiveProviders();
          setConnectionStatus('connected');
          return () => {
            setConnectionStatus('disconnected');
          };
        }
      } catch (error) {
        console.error('WebSocket connection error:', error);
        setConnectionStatus('error');
        message.error('Failed to connect to location tracking service');
      }
    };

    const cleanup = initTracking();
    return cleanup;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTracking]);

  useEffect(() => {
    if (selectedProviderId && providers.length > 0) {
      const provider = providers.find((p) => p.provider_id === selectedProviderId);
      if (provider) {
        setSelectedProvider(provider);
        setSelectedDeliveryPerson(null);
        setMapCenter({ lat: provider.latitude, lng: provider.longitude, initialized: true });
        fetchLocationHistory(selectedProviderId);
      }
    }
  }, [selectedProviderId, providers.length, providers]);

  useEffect(() => {
    if (selectedDeliveryPersonId && deliveryPersonnel.length > 0) {
      const dp = deliveryPersonnel.find((d) => d.delivery_man_id === selectedDeliveryPersonId);
      if (dp) {
        setSelectedDeliveryPerson(dp);
        setSelectedProvider(null);
        setMapCenter({
          lat: parseFloat(dp.latitude),
          lng: parseFloat(dp.longitude),
          initialized: true,
        });
      }
    }
  }, [selectedDeliveryPersonId, deliveryPersonnel.length, deliveryPersonnel]);

  const fetchActiveProviders = async () => {
    try {
      const response = await api.get('/admin/location/active-providers');
      const providersData = response.data.data || [];

      setProviders(providersData);

      if (providersData.length > 0 && !mapCenter.initialized) {
        setMapCenter({
          lat: providersData[0].latitude,
          lng: providersData[0].longitude,
          initialized: true,
        });
      }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching active providers:', error);
      setLoading(false);
    }
  };

  const fetchDeliveryLocations = async () => {
    try {
      const response = await api.get('/admin/live-locations');
      const data = response.data.data || response.data || [];
      const locations = Array.isArray(data) ? data : [];
      setDeliveryPersonnel(locations);

      if (locations.length > 0 && !mapCenter.initialized && providers.length === 0) {
        setMapCenter({
          lat: parseFloat(locations[0].latitude),
          lng: parseFloat(locations[0].longitude),
          initialized: true,
        });
      }
    } catch (error) {
      console.error('Error fetching delivery locations:', error);
    }
  };

  // Initial fetch of delivery locations
  useEffect(() => {
    fetchDeliveryLocations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchLocationHistory = async (providerId) => {
    try {
      const response = await api.get(`/admin/location/history/${providerId}?hours=24`);
      setLocationHistory((prev) => ({
        ...prev,
        [providerId]: response.data.data?.history || [],
      }));
    } catch (error) {
      console.error('Error fetching location history:', error);
    }
  };

  const getConnectionColor = () => {
    switch (connectionStatus) {
      case 'connected':
        return 'green';
      case 'connecting':
        return 'orange';
      case 'error':
        return 'red';
      default:
        return 'default';
    }
  };

  const getConnectionText = () => {
    switch (connectionStatus) {
      case 'connected':
        return 'Live Tracking';
      case 'connecting':
        return 'Connecting...';
      case 'error':
        return 'Connection Error';
      default:
        return 'Disconnected';
    }
  };

  const getTimeAgo = (timestamp) => {
    if (!timestamp) return 'N/A';
    const now = new Date();
    const time = new Date(timestamp);
    const diff = Math.floor((now - time) / 1000);
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} hours ago`;
    return `${Math.floor(diff / 86400)} days ago`;
  };

  const handleProviderSelect = (providerId) => {
    const provider = providers.find((p) => p.provider_id === providerId);
    setSelectedProvider(provider);
    fetchLocationHistory(providerId);
    if (provider) {
      setMapCenter({ lat: provider.latitude, lng: provider.longitude, initialized: true });
    }
  };

  const toggleTracking = () => {
    setIsTracking(!isTracking);
  };

  const refreshMap = () => {
    setLoading(true);
    fetchActiveProviders();
  };

  return (
    <div>
      <Card
        title="Real-Time Provider Tracking"
        extra={
          <Space>
            <Tag color={getConnectionColor()}>
              <EnvironmentOutlined />
              {getConnectionText()}
            </Tag>
            <Button
              icon={isTracking ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
              onClick={toggleTracking}
              type={isTracking ? 'default' : 'primary'}
            >
              {isTracking ? 'Pause' : 'Resume'}
            </Button>
            <Button icon={<ReloadOutlined />} onClick={refreshMap} loading={loading}>
              Refresh
            </Button>
            <Button
              icon={<FullscreenOutlined />}
              onClick={() => message.info('Full screen map mode coming soon!')}
            >
              Fullscreen
            </Button>
          </Space>
        }
      >
        <div
          style={{
            width: '100%',
            height: '500px',
            marginBottom: '16px',
            borderRadius: '6px',
            overflow: 'hidden',
          }}
        >
          {loading ? (
            <div
              style={{
                width: '100%',
                height: '500px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#f0f0f0',
              }}
            >
              <Spin size="large" />
            </div>
          ) : providers.length === 0 && deliveryPersonnel.length === 0 ? (
            <div
              style={{
                width: '100%',
                height: '500px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#f0f0f0',
                color: '#666',
                flexDirection: 'column',
              }}
            >
              <EnvironmentOutlined style={{ fontSize: '50px', marginBottom: '16px' }} />
              <h3>No Active Locations</h3>
              <p>Waiting for providers or delivery personnel to come online...</p>
            </div>
          ) : (
            <MapContainer
              center={[mapCenter.lat, mapCenter.lng]}
              zoom={13}
              style={{ width: '100%', height: '500px' }}
              ref={mapRef}
            >
              <ChangeView center={[mapCenter.lat, mapCenter.lng]} />
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {providers.map((provider) => (
                <Marker
                  key={`provider-${provider.provider_id}`}
                  position={[provider.latitude, provider.longitude]}
                  icon={provider.status === 'active' ? activeIcon : idleIcon}
                  eventHandlers={{
                    click: () => handleProviderSelect(provider.provider_id),
                  }}
                >
                  <Popup>
                    <div>
                      <strong>{provider.provider_name}</strong>
                      <br />
                      📍 {provider.address || 'No address'}
                      <br />
                      Last seen: {getTimeAgo(provider.last_update)}
                      <br />
                      Status:{' '}
                      <Tag color={provider.status === 'active' ? 'green' : 'default'}>
                        {provider.status}
                      </Tag>
                    </div>
                  </Popup>
                </Marker>
              ))}
              {deliveryPersonnel.map((dp) => (
                <Marker
                  key={`delivery-${dp.delivery_man_id}`}
                  position={[parseFloat(dp.latitude), parseFloat(dp.longitude)]}
                  icon={deliveryIcon}
                  eventHandlers={{
                    click: () => {
                      setSelectedDeliveryPerson(dp);
                      setSelectedProvider(null);
                    },
                  }}
                >
                  <Popup>
                    <div>
                      <strong>{dp.full_name || dp.name || 'Delivery Person'}</strong>
                      <br />
                      📍 {dp.address || 'No address'}
                      <br />
                      Last seen: {getTimeAgo(dp.timestamp)}
                      <br />
                      Status: <Tag color="blue">On Delivery</Tag>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          )}
        </div>

        <div style={{ display: 'flex', gap: '16px' }}>
          <div style={{ flex: 1 }}>
            <h4>Active Providers ({providers.length})</h4>
            <Select
              style={{ width: '100%', marginBottom: '8px' }}
              placeholder="Select a provider to track"
              onChange={handleProviderSelect}
              value={selectedProvider?.provider_id}
              showSearch
              optionFilterProp="children"
            >
              {providers.map((provider) => (
                <Option key={provider.provider_id} value={provider.provider_id}>
                  {provider.provider_name}
                </Option>
              ))}
            </Select>
            <div style={{ maxHeight: '120px', overflowY: 'auto' }}>
              {providers.map((provider) => (
                <div
                  key={provider.provider_id}
                  style={{
                    padding: '8px',
                    border: '1px solid #f0f0f0',
                    borderRadius: '4px',
                    marginBottom: '8px',
                    cursor: 'pointer',
                    backgroundColor:
                      selectedProvider?.provider_id === provider.provider_id ? '#e6f7ff' : 'white',
                  }}
                  onClick={() => handleProviderSelect(provider.provider_id)}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <div>
                      <strong>{provider.provider_name}</strong>
                      <br />
                      <small style={{ color: '#666' }}>📍 {provider.address || 'No address'}</small>
                      <br />
                      <small style={{ color: '#999' }}>
                        Last seen: {getTimeAgo(provider.last_update)}
                      </small>
                    </div>
                    <Tag color={provider.status === 'active' ? 'green' : 'default'}>
                      <EnvironmentOutlined />
                      {provider.status === 'active' ? 'Active' : 'Idle'}
                    </Tag>
                  </div>
                </div>
              ))}
            </div>

            <h4 style={{ marginTop: '16px' }}>Active Delivery ({deliveryPersonnel.length})</h4>
            <div style={{ maxHeight: '120px', overflowY: 'auto' }}>
              {deliveryPersonnel.map((dp) => (
                <div
                  key={dp.delivery_man_id}
                  style={{
                    padding: '8px',
                    border: '1px solid #f0f0f0',
                    borderRadius: '4px',
                    marginBottom: '8px',
                    cursor: 'pointer',
                    backgroundColor:
                      selectedDeliveryPerson?.delivery_man_id === dp.delivery_man_id
                        ? '#e6f7ff'
                        : 'white',
                  }}
                  onClick={() => {
                    setSelectedDeliveryPerson(dp);
                    setSelectedProvider(null);
                    setMapCenter({
                      lat: parseFloat(dp.latitude),
                      lng: parseFloat(dp.longitude),
                      initialized: true,
                    });
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <div>
                      <strong>{dp.full_name || dp.name || 'Delivery Person'}</strong>
                      <br />
                      <small style={{ color: '#999' }}>Last seen: {getTimeAgo(dp.timestamp)}</small>
                    </div>
                    <Tag color="blue">
                      <CarOutlined />
                      On Delivery
                    </Tag>
                  </div>
                </div>
              ))}
              {deliveryPersonnel.length === 0 && (
                <div style={{ color: '#999', padding: '8px' }}>No active delivery personnel</div>
              )}
            </div>
          </div>

          {selectedProvider && (
            <div style={{ flex: 1 }}>
              <h4>Provider Details</h4>
              <div style={{ padding: '8px', backgroundColor: '#f9f9f9', borderRadius: '4px' }}>
                <p>
                  <strong>Name:</strong> {selectedProvider.provider_name}
                </p>
                <p>
                  <strong>Location:</strong> {selectedProvider.latitude?.toFixed(6)},{' '}
                  {selectedProvider.longitude?.toFixed(6)}
                </p>
                <p>
                  <strong>Address:</strong> {selectedProvider.address || 'Not specified'}
                </p>
                <p>
                  <strong>Status:</strong>{' '}
                  <Tag color={selectedProvider.status === 'active' ? 'green' : 'default'}>
                    {selectedProvider.status}
                  </Tag>
                </p>
                <p>
                  <strong>Last Update:</strong>{' '}
                  {selectedProvider.last_update
                    ? new Date(selectedProvider.last_update).toLocaleString()
                    : 'N/A'}
                </p>

                <Button
                  size="small"
                  style={{ marginTop: '8px' }}
                  onClick={() => {
                    window.open(
                      `https://maps.google.com/?q=${selectedProvider.latitude},${selectedProvider.longitude}`,
                      '_blank',
                    );
                  }}
                >
                  View on Google Maps
                </Button>
              </div>

              {locationHistory[selectedProvider.provider_id] &&
                locationHistory[selectedProvider.provider_id].length > 0 && (
                  <div style={{ marginTop: '16px' }}>
                    <h5>Location History (Last 24 Hours)</h5>
                    <div style={{ maxHeight: '200px', overflowY: 'auto', fontSize: '14px' }}>
                      {locationHistory[selectedProvider.provider_id]
                        .slice(0, 10)
                        .map((point, index) => (
                          <div
                            key={index}
                            style={{ padding: '4px', borderBottom: '1px solid #f0f0f0' }}
                          >
                            <small>
                              {point.created_at
                                ? new Date(point.created_at).toLocaleTimeString()
                                : point.timestamp
                                  ? new Date(point.timestamp).toLocaleTimeString()
                                  : 'N/A'}{' '}
                              -{parseFloat(point.latitude)?.toFixed(6)},{' '}
                              {parseFloat(point.longitude)?.toFixed(6)}
                              {point.speed && ` - ${point.speed} km/h`}
                            </small>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
            </div>
          )}

          {selectedDeliveryPerson && !selectedProvider && (
            <div style={{ flex: 1 }}>
              <h4>Delivery Person Details</h4>
              <div style={{ padding: '8px', backgroundColor: '#f9f9f9', borderRadius: '4px' }}>
                <p>
                  <strong>Name:</strong>{' '}
                  {selectedDeliveryPerson.full_name || selectedDeliveryPerson.name || 'N/A'}
                </p>
                <p>
                  <strong>Location:</strong>{' '}
                  {parseFloat(selectedDeliveryPerson.latitude)?.toFixed(6)},{' '}
                  {parseFloat(selectedDeliveryPerson.longitude)?.toFixed(6)}
                </p>
                <p>
                  <strong>Address:</strong> {selectedDeliveryPerson.address || 'Not specified'}
                </p>
                <p>
                  <strong>Status:</strong> <Tag color="blue">On Delivery</Tag>
                </p>
                <p>
                  <strong>Last Update:</strong>{' '}
                  {selectedDeliveryPerson.timestamp
                    ? new Date(selectedDeliveryPerson.timestamp).toLocaleString()
                    : 'N/A'}
                </p>

                <Button
                  size="small"
                  style={{ marginTop: '8px' }}
                  onClick={() => {
                    window.open(
                      `https://maps.google.com/?q=${selectedDeliveryPerson.latitude},${selectedDeliveryPerson.longitude}`,
                      '_blank',
                    );
                  }}
                >
                  View on Google Maps
                </Button>
              </div>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default RealTimeLocationMap;
